import React, {useCallback, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridExoticColumnFields
} from "../../../../../../../packages/data-grid";
import {InventoryAdjustmentRecordsDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ChevronRightRounded, OpenInNew} from "@mui/icons-material";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../containers/data-grid";
import InventoryAdjustmentRecordSearchSection
    from "../../../../../../components/search-sections/inventory-adjustment-records-search-section";
import classnames from "classnames";
import SimpleMoreDetailsDialog from "../../../../../../components/dialogs/simple-more-details-dialog";
import Chips, {ChipsContentType, ChipsTypes} from "../../../../../../components/base/chips/chips";
import Api from "../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import useSearch from "../../../../../../hooks/use-search";
import DateUtils from "../../../../../../../core/services/date-utils";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {Link} from "react-router-dom";


const dataGridSaveConfig = {
    name: 'InventoryAdjustmentRecordViewDataGrid',
    version: 2,
}

const exportingChunkSize = 100;

const InventoryAdjustmentRecordView = ({loading: loadingProp, product}) => {
    const [records, setRecords] = useState([]);
    const [fullNoteDialogOpen, setFullNoteDialogOpen] = useState(false);
    const [fullNote, setFullNote] = useState(null);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportInventoryAdjustments = operatorAccessLevels.packages.bizInventory.exportInventoryAdjustments;

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.bizInventory.adjustmentInformation ? [
                {
                    name: InventoryAdjustmentRecordsDataGridCellKeys.view,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: false,
                    sortable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
            ] : []),
            {
                name: DataGridExoticColumnFields.detailedPanelToggler,
            },
            {
                title: "Code",
                name: InventoryAdjustmentRecordsDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Type",
                name: InventoryAdjustmentRecordsDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 60,
                sortable: false,
            },
            {
                title: "Part",
                name: InventoryAdjustmentRecordsDataGridCellKeys.part,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 90,
            },
            {
                title: "Quantity",
                name: InventoryAdjustmentRecordsDataGridCellKeys.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 80,
            },
            {
                title: "Quantity Change",
                name: InventoryAdjustmentRecordsDataGridCellKeys.quantityChange,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: "New Quantity",
                name: InventoryAdjustmentRecordsDataGridCellKeys.newQuantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 110,
                sortable: false,
            },
            {
                title: "Average Cost",
                name: InventoryAdjustmentRecordsDataGridCellKeys.averageCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: "Cost",
                name: InventoryAdjustmentRecordsDataGridCellKeys.cost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: "Extended Cost",
                name: InventoryAdjustmentRecordsDataGridCellKeys.extendedCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 110,
                sortable: false,
            },
            {
                title: "Date & Time",
                name: InventoryAdjustmentRecordsDataGridCellKeys.dateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                width: 100,
            },
            {
                title: "Operator",
                name: InventoryAdjustmentRecordsDataGridCellKeys.operator,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
                sortable: false,
            },
            {
                title: "Warehouse",
                name: InventoryAdjustmentRecordsDataGridCellKeys.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Reason Code",
                name: InventoryAdjustmentRecordsDataGridCellKeys.reasonCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Reason",
                name: InventoryAdjustmentRecordsDataGridCellKeys.reason,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 150,
                sortable: false,
            },
            {
                title: "Note",
                name: InventoryAdjustmentRecordsDataGridCellKeys.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 400,
                sortable: false,
            },
            {
                name: DataGridExoticColumnFields.detailedPanel,
            }
        ], [operatorAccessLevels.packages.bizInventory.adjustmentInformation])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Prepares the api request body
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        const forApi = {
            partIds: product?.id ? [product?.id] : args.parts?.map(e => e.id) ?? [],
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword?.toString(),
            parentCategoryIds: JSON.parse(args.filters?.categories ?? '[]').map(e => e.id),
            reasons: JSON.parse(args.filters?.reason ?? '[]').map(e => e.id),
            isAdd: args.filters?.type === 1 ? true : args.filters?.type === 2 ? false : null,
            startDateTime: args.filters?.startDate,
            endDateTime: args.filters?.endDate,
            sorts: [],
        };

        if (args.orderBy?.field) {
            forApi['sorts'] = [
                {
                    property: args.orderBy.field,
                    isDescending: args.orderBy.descending,
                }
            ]
        }

        if (!forApi.partIds.length)
            forApi.partIds = null;
        if (!forApi.parentCategoryIds.length)
            forApi.parentCategoryIds = null;
        if (!forApi.reasons.length)
            forApi.reasons = null;

        return forApi;
    }, [product?.id])

    /**
     * Fetches the list of inventory adjustment records from server
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getInventoryAdjustmentItems(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.data) {
            response.data.items = response.data.items.map(e => ({
                ...e,
                quantity: Number(e?.oldQty),
                quantityChange: Number(e.qty),
                newQuantity: Number(e?.oldQty) + (e.isAdd ? 1 : -1) * Number(e.qty),
                extendedCost: Number(e.qty) * e.cost * (e.isAdd ? 1 : -1),
            }))
        }
        if (response?.resultFlag && !forExport) {
            setRecords(response?.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        parts: [],
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Width of the serial numbers wrapper in the expandable section of data grid
     * @type {string}
     */
    const expandableSectionWidth = useMemo(() => {
        const dataGridHeader = document.getElementsByClassName('data-grid-toolbar')?.[0];
        return `${dataGridHeader?.offsetWidth}px` ?? '0';
    }, [searchData.loading, records])

    /**
     * Memo version of the indicator for checking if the view is loading or not
     * @type {unknown}
     */
    const loading = useMemo(() => loadingProp || searchData.loading, [loadingProp, searchData.loading])

    /**
     * Formats the list of lost search reports into csv format
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Inventory Adjustment Records`;
        return {
            title: exportTitle,
            body: list?.map((item, index) => ({
                "": index + 1,
                'code': item?.adjustmentCode,
                'type': item?.isAdd ? 'Add' : 'Remove',
                'part': item?.partNumber,
                'quantity': item.quantity,
                'Quantity Change': item.quantityChange,
                'New Quantity': item.newQuantity,
                'Average Cost': formatMoney(item?.avgCost),
                'Cost': !Number.isNaN(item?.cost) ? formatMoney(item?.cost) : '--',
                'Extended Cost': formatMoney(item.extendedCost),
                'Submitted DateTime': DateUtils.serverToLocal(item?.submittedDateTime).format(DateUtils.dateTimeFormat),
                'Operator': item?.submittedBy,
                'Warehouse Code': item?.warehouseCode,
                'Reason Code': item?.reason?.code,
                'Reason': item?.reason?.title,
                'Note': item?.description,
                "Serial Numbers": item.serials?.reduce((a, s, index, array) =>
                    a.concat(s.number).concat(index !== array.length - 1 ? ' | ' : ''), '') ?? '--',
            })) ?? [],
        }
    }, [])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Opens a dialog for showing the note full details
     * @param note
     */
    const viewFullNote = (note) => {
        setFullNote(note);
        setFullNoteDialogOpen(true);
    }

    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return records?.map((record) => ({
            key: record.id,
            data: record,
            cells: {
                [InventoryAdjustmentRecordsDataGridCellKeys.view]:
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.inventory.inventoryAdjustment.single(record.adjustmentId)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [DataGridExoticColumnFields.detailedPanelToggler]: null,
                [InventoryAdjustmentRecordsDataGridCellKeys.code]: record?.adjustmentCode,
                [InventoryAdjustmentRecordsDataGridCellKeys.type]: record?.isAdd ? 'Add' : 'Remove',
                [InventoryAdjustmentRecordsDataGridCellKeys.part]:
                    <ProductInformationPopover partNo={record?.partNumber}>
                        {record?.partNumber ?? '--'}
                    </ProductInformationPopover>,
                [InventoryAdjustmentRecordsDataGridCellKeys.quantity]: record?.quantity,
                [InventoryAdjustmentRecordsDataGridCellKeys.newQuantity]: record.newQuantity,
                [InventoryAdjustmentRecordsDataGridCellKeys.quantityChange]: record.quantityChange,
                [InventoryAdjustmentRecordsDataGridCellKeys.averageCost]: record?.avgCost,
                [InventoryAdjustmentRecordsDataGridCellKeys.cost]: record?.cost,
                [InventoryAdjustmentRecordsDataGridCellKeys.extendedCost]: record.extendedCost,
                [InventoryAdjustmentRecordsDataGridCellKeys.dateTime]: record?.submittedDateTime,
                [InventoryAdjustmentRecordsDataGridCellKeys.operator]: record?.submittedBy,
                [InventoryAdjustmentRecordsDataGridCellKeys.warehouse]: record?.warehouseCode,
                [InventoryAdjustmentRecordsDataGridCellKeys.reasonCode]: record?.reason?.code,
                [InventoryAdjustmentRecordsDataGridCellKeys.reason]: record?.reason?.title,
                [InventoryAdjustmentRecordsDataGridCellKeys.note]: record?.description?.length > 30
                    ? <div className={'note-container'}>
                        <p {...dataGridCellTextProps} className={'note'}>
                            {`${record?.description.substring(0, 25)}...`}
                        </p>
                        <div
                            className={classnames('part-number-text-button')}
                            onClick={() => viewFullNote(record?.description)}
                        >
                            <p className={'mb-1'}>More</p>
                            <ChevronRightRounded className='icon small' color={"primary"}/>
                        </div>
                    </div>
                    :
                    <p {...dataGridCellTextProps}>{record?.description}</p>,
                [DataGridExoticColumnFields.detailedPanel]: !record?.serials?.length
                    ? null
                    : <div
                        className={'serial-numbers-wrapper'}
                        style={{
                            width: expandableSectionWidth,
                        }}
                    >
                        {
                            record?.serials?.map(e =>
                                <span {...dataGridCellTextProps} key={e.id} className={'mr-2 mb-2'}>
                                    <Chips
                                        contentType={ChipsContentType.text}
                                        type={ChipsTypes.none}
                                        content={`${e.number} (${formatMoney(e.cost)})`}
                                    />
                                </span>
                            )
                        }
                    </div>,
            }
        }))
    }, [records, expandableSectionWidth])

    return (
        <>
            <div className={'inventory-adjustment-record-view'}>
                <div className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-2'}>
                    {
                        !product &&
                        <p className={'view-title'}>
                            System Inventory Adjustment Items
                        </p>
                    }
                </div>
                <InventoryAdjustmentRecordSearchSection
                    searchData={searchData}
                    singleProduct={!!product}
                    defaultProduct={product}
                />
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: loading},
                        density: dataGridDensity,
                    }}
                    hideFooterActions
                    getExportInfo={canExportInventoryAdjustments ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <SimpleMoreDetailsDialog
                open={fullNoteDialogOpen}
                setOpen={setFullNoteDialogOpen}
                title={'Note'}
                details={fullNote}
            />
        </>
    );
}

export default InventoryAdjustmentRecordView;
